<template>
  <Layout>
    <PageHeader class="page-header" :title="title" :subTitle="subTitle">
      <template #afterTitle>
        <McButton
          v-if="!(hasAccessFleetTask && hasAccessFleetTaskInstant)"
          variant="primary"
          :disabled="isLoading"
          :text="$t('fleetTask.scheduleDelivery')"
          @click="handleAdd"
        />
        <b-dropdown
          v-if="hasAccessFleetTask && hasAccessFleetTaskInstant"
          class="create-dropdown"
          toggle-tag="div"
          toggle-class="p-0 bg-transparent"
          :disabled="isLoading"
        >
          <template #button-content>
            <McButton
              variant="primary"
              icon="icon-outline_chevron-down"
              icon-position="right"
              text="Jadwalkan Penugasan"
              :disabled="isLoading"
            />
          </template>
          <b-dropdown-item
            v-for="item in createDropdownItems"
            :key="item.key"
            class="create-dropdown-item"
            @click="$router.push({ name: item.routeName })"
          >
            {{ item.label }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </PageHeader>

    <div class="fleet-availability__header">
      <StatusLegend />
      <div class="d-flex gap-2">
        <div class="fleet-availability__filter-buttons">
          <b-form-radio-group
            id="btn-radios-1"
            v-model="radioGroupSelected"
            name="radios-btn-default"
            buttons
            button-variant="outline-primary"
            :disabled="isLoading"
            :options="radioGroupOptions"
          />
        </div>

        <!-- TODO: For enhancement feature -->
        <McTableFilter
          v-if="false"
          :parseFilterDataMappings="parseFilterDataMappings"
          :activeFilters="activeFilters"
          :isDisabled="isLoading"
          @submit="handleChangeActiveFilters"
        >
          <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
            <InputTableFilter
              :formValues="formValues"
              :fullObjects="fullObjects"
              @update:formValues="updateFormValues($event)"
              @update:fullObjects="updateFullObjects($event)"
            />
          </template>
        </McTableFilter>
      </div>
    </div>

    <div class="fleet-availability__wrapper">
      <div class="fleet-availability__header">
        <div></div>
        <div class="time-selected">{{ computedTimeSelected }}</div>
        <div class="d-flex align-items-center gap-2">
          <PeriodTypeOptions :type.sync="periodType" />
          <b-button-group v-if="periodType == 'hour'" class="fleet-availability__toggle-day-btns">
            <McButton
              class="__toggle-prev"
              variant="secondary"
              text=""
              icon="icon-outline_chevron-left"
              :disabled="isLoading"
              @click="handleChangeDate('incrementDay')"
            />
            <McButton
              class="__toggle-today"
              variant="secondary"
              :text="$t('fleetAvailability.action.today')"
              :disabled="isLoading"
              @click="handleChangeDate('today')"
            />
            <McButton
              class="__toggle-next"
              variant="secondary"
              text=""
              icon="icon-outline_chevron-right"
              :disabled="isLoading"
              @click="handleChangeDate('decrementDay')"
            />
          </b-button-group>

          <div class="position-relative">
            <McButton
              text=""
              variant="secondary"
              icon="icon-outline_calendar"
              :disabled="isLoading"
              @click="openDatePicker"
            />
            <!-- Real Date Picker is placed behind McButton -->
            <FormulateInput
              :key="periodType"
              ref="custom-date-picker"
              v-model="timeSelected"
              type="date-picker"
              class="p-0 h-auto custom-date-picker"
              format="DD MMM YYYY"
              value-type="YYYY-MM-DDTHH:mm:ssZ"
              :range="periodType == 'day'"
              :draft="true"
              :shortcuts="datepickerShorcuts"
              :confirm="true"
              :cancel="true"
              :confirmText="$t('global.apply')"
            />
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="busy">
        <b-spinner class="align-middle mb-2" />
        <strong>{{ $t('global.text.loading') }}...</strong>
      </div>
      <template v-else>
        <McTimelineHourly
          v-if="periodType == 'hour'"
          :resourceField="resourceField"
          :resources="computedResources"
          :colorMappings="eventColorMappings"
        />
        <TimelineTableDaily
          v-if="periodType == 'day'"
          :resourceField="resourceField"
          :resources="newComputedResources"
          :colorMappings="eventColorMappings"
        />
      </template>
    </div>
  </Layout>
</template>

<script>
import { computed } from 'vue'
import moment from 'moment'

import debounce from 'lodash/debounce'
import get from 'lodash/get'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import McTimelineHourly from './components/McTimelineHourly'
import TimelineTableDaily from './components/TimelineTableDaily'
import PeriodTypeOptions from './components/PeriodTypeOptions'
import StatusLegend from './components/StatusLegend.vue'
import McTableFilter from '@/components/McTableFilter'
import InputTableFilter from './components/InputTableFilter'

import FleetTaskTimeline from '@/services/rest-api/lib/fleet-availability/fleet-task-timeline.js'
import appConfig from '@/app.config'
import eventColorMappings from './lib/constants/event-color-mappings'
import parseFilterDataMappings from './lib/constants/parse-data-filter-mapping'
import datepickerShorcuts from './lib/constants/date-picker-shortcuts'
import { formatTime } from './lib/helper/format-time'

import cloneDeep from 'lodash/cloneDeep'
import { mapGetters } from 'vuex'

export default {
  page: {
    title: 'Fleet Availability',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McTimelineHourly,
    TimelineTableDaily,
    PeriodTypeOptions,
    StatusLegend,
    McTableFilter,
    InputTableFilter,
  },
  provide() {
    return {
      timeSelected: computed(() => {
        if (Array.isArray(this.timeSelected)) {
          return this.timeSelected.map((el) => moment(el))
        }

        return moment(this.timeSelected)
      }),
      periodLength: computed(() => this.periodLength),
    }
  },
  data() {
    return {
      eventColorMappings,
      parseFilterDataMappings,

      timeSelected: moment().format('YYYY-MM-DDTHH:mm:ssZ'),
      radioGroupSelected: 'vehicle',
      periodType: 'hour',

      isLoading: false,
      timeInterval: null,

      activeFilters: {},
      showResponse: null,
    }
  },
  computed: {
    ...mapGetters({
      isMetrack: 'identity/isMetrack',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    hasAccessFleetTask() {
      return this.hasAccessAll(['tms:read', 'tms:create', 'tms:update', 'tms:delete'])
    },
    hasAccessFleetTaskInstant() {
      return this.hasAccessAll([
        'fleet-task-instant:read',
        'fleet-task-instant:create',
        'fleet-task-instant:update',
        'fleet-task-instant:delete',
      ])
    },
    createDropdownItems() {
      return [
        {
          key: 'fleet-task-create',
          routeName: 'Fleet Task Create',
          label: 'Dengan DO',
        },
        {
          key: 'fleet-task-instant-create',
          routeName: 'Fleet Task Instant Create',
          label: 'Tanpa DO',
        },
      ]
    },
    title() {
      return this.$t('fleetAvailability.title')
    },
    subTitle() {
      return this.$t('fleetAvailability.subTitle')
    },
    radioGroupOptions() {
      return [
        {
          html: `<i class="icon-outline_car"></i><span>${this.$t(
            'fleetAvailability.action.fleet'
          )}</span>`,
          value: 'vehicle',
          label: this.$t('fleetAvailability.fields.licensePlate'),
          key: 'vehicle.license_plate',
          getRows: (item) => {
            return [
              `<i class="mc-icon icon-outline_clock"></i><div>${formatTime(
                item.start
              )} - ${formatTime(item.end)}</div>`,
              item.driver?.name
                ? `<i class="mc-icon icon-outline_driver"></i><div>${item.driver?.name}</div>`
                : '',
            ]
          },
        },
        {
          html: `<i class="icon-outline_driver"></i><span>${this.$t(
            'fleetAvailability.action.driver'
          )}</span>`,
          value: 'driver',
          label: this.$t('fleetAvailability.fields.driver'),
          key: 'driver.name',
          getRows: (item) => {
            return [
              `<i class="mc-icon icon-outline_clock"></i><div>${formatTime(
                item.start
              )} - ${formatTime(item.end)}</div>`,
              `<i class="mc-icon icon-outline_car"></i><div>${item.vehicle?.license_plate}</div>`,
            ]
          },
        },
      ]
    },
    resourceField() {
      return this.radioGroupOptions.find((option) => option.value == this.radioGroupSelected)
    },
    computedTimeSelected() {
      if (this.periodType == 'day') {
        const startDate = moment(this.timeSelected[0])
          .locale(this.$i18n.locale)
          .format('DD MMMM YYYY')
        const endDate = moment(this.timeSelected[1])
          .locale(this.$i18n.locale)
          .format('DD MMMM YYYY')
        return `${startDate} - ${endDate}`
      }

      return moment(this.timeSelected).locale(this.$i18n.locale).format('DD MMMM YYYY')
    },
    computedResources() {
      const modified = []
      const resourceTaken = new Set()

      this.showResponse?.forEach((resourceItem) => {
        const newTimeline = {
          resourceValue: get(resourceItem, this.resourceField.key),
          isShown: true,
          rowSpan: resourceItem.timelines.length,
          events: [],
        }

        // If there is no events
        if (resourceItem.timelines.length == 0) {
          newTimeline.rowSpan = 1
          modified.push(newTimeline)

          return
        }

        // If there are events in timelines
        resourceItem.timelines.forEach((timeline) => {
          const taken = resourceTaken.has(resourceItem[this.radioGroupSelected]?.id)

          newTimeline.isShown = !taken
          newTimeline.rowSpan = resourceItem.timelines.length
          newTimeline.events = timeline?.map((timelineItem) => {
            return {
              ...timelineItem,
              rows: this.resourceField.getRows(timelineItem),
            }
          })

          resourceTaken.add(resourceItem[this.radioGroupSelected]?.id)
          modified.push(cloneDeep(newTimeline))
        })
      })

      return modified
    },
    newComputedResources() {
      const modified = []

      this.showResponse?.forEach((resourceItem) => {
        const newResource = {
          resourceValue: get(resourceItem, this.resourceField.key),
          rowSpan: resourceItem.timelines.length || 1,
          timelines: [],
        }

        // If there is no events
        if (resourceItem.timelines.length == 0) {
          modified.push(newResource)

          return
        }

        // If there are events in timelines
        resourceItem.timelines.forEach((timeline) => {
          const events = timeline?.map((timelineItem) => {
            return {
              ...timelineItem,
              rows: this.resourceField.getRows(timelineItem),
            }
          })

          newResource.timelines.push(events)
        })

        modified.push(cloneDeep(newResource))
      })

      return modified
    },
    computedFilters() {
      return {
        timeSelected: this.timeSelected,
        radioGroupSelected: this.radioGroupSelected,
        periodType: this.periodType,
      }
    },
    datepickerShorcuts() {
      if (this.periodType == 'hour') return undefined
      return datepickerShorcuts()
    },
    periodLength() {
      let length = 1

      if (Array.isArray(this.timeSelected)) {
        length = moment(this.timeSelected[1]).diff(moment(this.timeSelected[0]), 'd') + 1
      }

      return length
    },
  },
  watch: {
    computedFilters(newValue, oldValue) {
      if (newValue.periodType !== oldValue.periodType) {
        if (newValue.periodType == 'day') {
          const newTimeSelected = [
            moment(this.timeSelected).subtract(6, 'd').format('YYYY-MM-DDTHH:mm:ssZ'),
            this.timeSelected,
          ]

          this.timeSelected = newTimeSelected
        } else if (newValue.periodType == 'hour') {
          this.timeSelected = this.timeSelected[1]
        }

        return
      }

      this.handleFetchData()
    },
    periodType(newValue) {
      const query = { ...this.$route.query, periodType: newValue }

      this.$router.replace({ name: 'Fleet Availability', query })
    },
    radioGroupSelected(newValue) {
      const query = { ...this.$route.query, type: newValue }

      this.$router.replace({ name: 'Fleet Availability', query })
    },
  },
  created() {
    const query = this.$route.query
    if (Object.keys(query).length > 0) {
      this.radioGroupSelected = query.type
      this.periodType = query.periodType

      return
    }

    const newQuery = {
      type: this.radioGroupSelected,
      periodType: this.periodType,
    }

    this.$router.replace({ name: 'Fleet Availability', query: newQuery })
  },
  mounted() {
    this.handleFetchData()
  },
  beforeDestroy() {
    this.clearTimeInterval()
    window.removeEventListener('focus', this.fetchOnTabFocus)
    window.removeEventListener('blur', this.clearTimeInterval)
  },
  methods: {
    handleAdd() {
      const name = this.hasAccessFleetTask ? 'Fleet Task Create' : 'Fleet Task Instant Create'

      this.$router.push({ name })
    },
    async getData(source) {
      if (source === 'initial') {
        this.isLoading = true
      }

      let interval = ''
      let date = null

      if (this.periodType == 'hour') {
        interval = 'day'
        date = moment(this.timeSelected).format('DD-MM-yyyy')
      } else if (this.periodType == 'day') {
        interval = `-${this.periodLength}-day`
        date = moment(this.timeSelected[1]).format('DD-MM-yyyy')
      }

      const res = await FleetTaskTimeline.get({
        date,
        interval,
        group: this.radioGroupSelected,
      })

      this.isLoading = false

      if (res.errors) return

      this.showResponse = res.data
    },
    handleChangeDate: debounce(function (type) {
      const dateFormat = 'YYYY-MM-DDTHH:mm:ssZ'

      switch (type) {
        case 'today':
          this.timeSelected = moment().format(dateFormat)
          break
        case 'incrementDay':
          this.timeSelected = moment(this.timeSelected).subtract(1, 'd').format(dateFormat)
          break
        case 'decrementDay':
          this.timeSelected = moment(this.timeSelected).add(1, 'd').format(dateFormat)
          break
        default:
          break
      }
    }, 250),
    handleFetchData() {
      this.clearTimeInterval()
      this.fetchDataPeriodically()

      window.addEventListener('focus', this.fetchOnTabFocus)

      window.addEventListener('blur', this.clearTimeInterval)
    },
    clearTimeInterval() {
      clearInterval(this.timeInterval)
      this.timeInterval = null
    },
    fetchDataPeriodically(source) {
      this.getData(source ?? 'initial')
      this.timeInterval = setInterval(() => {
        this.getData('interval')
      }, 10000)
    },
    handleChangeActiveFilters(filters) {
      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null, undefined, or empty string
        if (filter.values === null || filter.values === undefined || filter.values === '') return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })
    },
    fetchOnTabFocus() {
      this.fetchDataPeriodically('focus')
    },
    openDatePicker() {
      /**
       * Trigger a focus event on datepicker input element to open the datepicker
       */
      const datepickerInput = this.$refs['custom-date-picker'].$el.querySelector('input')
      datepickerInput.focus()
    },
  },
}
</script>

<style lang="scss" scoped>
.page-header {
  .banner-header {
    margin: 0;
  }
}

.fleet-availability {
  &__wrapper {
    background-color: white;
    padding: 20px;
    border-radius: 4px;

    .busy {
      min-height: min(50vh, 500px);
      display: grid;
      place-items: center;
      place-content: center;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    margin-bottom: 20px;
    flex-wrap: wrap;

    .time-selected {
      font-size: 16px;
      font-weight: 600;
      color: $mep-color-text;
    }

    // To make a single calendar icon button appearance
    .custom-date-picker {
      position: absolute;
      z-index: -1;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;

      :deep(.mx-input-wrapper) {
        height: 38px;
        width: 38px;
        border: 1px solid $mep-primary;
        border-radius: 4px;
      }
    }
  }

  &__toggle-day-btns {
    :deep(.__toggle-prev) {
      .btn {
        border-radius: 4px 0 0 4px;
        border-right: 0;
      }
    }

    :deep(.__toggle-today) {
      .btn {
        border-radius: 0;
      }
    }

    :deep(.__toggle-next) {
      .btn {
        border-radius: 0 4px 4px 0;
        border-left: 0;
      }
    }
  }

  &__filter-buttons {
    :deep(input[type='radio']) {
      display: none;

      & + span {
        display: flex;

        i {
          margin-right: 4px;
          font-size: 20px;
        }
      }
    }
  }
}
</style>
